import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  LinearProgress,
  Grid,
  Container,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Search,
  Clear,
  Done,
  Warning,
  ArrowBack,
  Scanner,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface ClientItem {
  id: string;
  unitId: string;
  barcode: string;
  itemName: string;
  symbol: string;
  unitOfMeasurement: string;
  expectedQuantity: number;
  scannedQuantity: number;
  boxRefNumber: string;
  itemAdditionalProperties?: Record<string, any>;
}

interface ScannedItem {
  barcode: string;
  quantity: number;
  boxRefNumber: string;
}

type ScanStatus = 'none' | 'partial' | 'exact';

const getScanStatus = (scannedQty: number, expectedQty: number): ScanStatus => {
  if (scannedQty === 0) return 'none';
  if (scannedQty === expectedQty) return 'exact';
  return 'partial';
};

const rowBgFor = (status: ScanStatus) => {
  switch (status) {
    case 'exact':   return 'success.lighter';
    case 'partial': return 'warning.lighter';
    case 'none':    return 'inherit';
  }
};

const chipColorFor = (status: ScanStatus): 'default' | 'success' | 'warning' => {
  switch (status) {
    case 'exact':   return 'success';
    case 'partial': return 'warning';
    case 'none':    return 'default';
  }
};

const ClientReceptionValidation: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const scannerInputRef = useRef<HTMLInputElement>(null);

  // State
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [items, setItems] = useState<ClientItem[]>([]);
  const [scannedItems, setScannedItems] = useState<Map<string, ScannedItem>>(new Map());
  const [scanInput, setScanInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const totalItems = items.length;
  const totalExpectedQty = items.reduce((sum, item) => sum + item.expectedQuantity, 0);
  const totalScannedQty = Array.from(scannedItems.values()).reduce((sum, item) => sum + item.quantity, 0);
  const fullyScannedCount = items.filter(item => {
    const scanned = scannedItems.get(item.barcode);
    return scanned && scanned.quantity === item.expectedQuantity; // exact match only
  }).length;
  const progressPercent = totalItems > 0 ? (fullyScannedCount / totalItems) * 100 : 0;

  useEffect(() => {
    const loadItems = async () => {
      if (!clientId) {
        dispatch(showAlert({ message: 'Client ID is required', type: 'error' }));
        navigate('/clients');
        return;
      }

      setLoading(true);
      try {
        const response = await DataService.get(`api/Client/${clientId}/warehouse-inventory`);
        if (!response.ok) throw new Error('Failed to load client inventory');

        const json = await response.json();
        const inventoryItems = json?.data?.items || json?.items || [];

        const mappedItems: ClientItem[] = inventoryItems.map((item: any) => ({
          id: item.id,
          unitId: item.unit?.id || item.unitId,
          barcode: item.unit?.barcode || item.barcode,
          itemName: item.unit?.itemName || 'Unknown Item',
          symbol: item.unit?.symbol || '',
          unitOfMeasurement: item.unit?.unitOfMeasurement || 'Pcs',
          expectedQuantity: item.quantity || 0,
          scannedQuantity: 0,
          boxRefNumber: item.boxRefNumber || '',
          itemAdditionalProperties: item.unit?.itemAdditionalProperties || {},
        }));

        setItems(mappedItems);
      } catch (err: any) {
        dispatch(showAlert({ message: err.message || 'Failed to load items', type: 'error' }));
        console.error('Load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, [clientId, dispatch, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      scannerInputRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleScan = useCallback((barcode: string) => {
    if (!barcode.trim()) return;

    const trimmedBarcode = barcode.trim();
    const item = items.find(i => i.barcode === trimmedBarcode);

    if (!item) {
      dispatch(showAlert({ message: `Item not found: ${trimmedBarcode}`, type: 'warning' }));
      setScanInput('');
      return;
    }

    setScannedItems(prev => {
      const current = prev.get(trimmedBarcode)?.quantity ?? 0;
      const next = current + 1;

      if (next > item.expectedQuantity) {
        dispatch(showAlert({ message: `Cannot scan more than expected (${item.expectedQuantity}).`, type: 'warning' }));
        setScanInput('');
        return prev;
      }

      const newMap = new Map(prev);
      newMap.set(trimmedBarcode, {
        barcode: trimmedBarcode,
        quantity: next,
        boxRefNumber: item.boxRefNumber,
      });
      return newMap;
    });

    setScanInput('');
    dispatch(showAlert({ message: `Scanned: ${item.itemName}`, type: 'success' }));
  }, [items, dispatch]);

  const handleQuantityChange = (barcode: string, newQuantity: number) => {
    const item = items.find(i => i.barcode === barcode);
    if (!item) return;

    if (newQuantity < 0) newQuantity = 0;

    if (newQuantity > item.expectedQuantity) {
      dispatch(showAlert({
        message: `Quantity cannot exceed expected (${item.expectedQuantity}).`,
        type: 'warning'
      }));
      newQuantity = item.expectedQuantity;
    }

    setScannedItems(prev => {
      const newMap = new Map(prev);
      if (newQuantity === 0) newMap.delete(barcode);
      else {
        newMap.set(barcode, {
          barcode,
          quantity: newQuantity,
          boxRefNumber: item.boxRefNumber,
        });
      }
      return newMap;
    });
  };

  const handleValidate = async () => {
    if (scannedItems.size === 0) {
      dispatch(showAlert({ message: 'No items scanned', type: 'warning' }));
      return;
    }

    setValidating(true);
    try {
      const payload = {
        clientId: clientId!,
        items: Array.from(scannedItems.values()),
        notes: notes.trim() || undefined,
      };

      const response = await DataService.post('api/Operation/client-reception/validate', payload);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Validation failed');
      }

      dispatch(showAlert({ message: 'Client reception validated successfully!', type: 'success' }));
      setConfirmDialogOpen(false);

      setTimeout(() => {
        navigate(`/client/${clientId}/reception`);
      }, 1500);
    } catch (err: any) {
      dispatch(showAlert({ message: err.message || 'Failed to validate reception', type: 'error' }));
      console.error('Validation error:', err);
    } finally {
      setValidating(false);
    }
  };

  const filteredItems = items.filter(item =>
    searchTerm === '' ||
    item.barcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle row expansion
  const toggleRowExpansion = (barcode: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(barcode)) newSet.delete(barcode);
      else newSet.add(barcode);
      return newSet;
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate(`/client/${clientId}/reception`)} color="primary">
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" fontWeight="600">
            Client Reception Validation
          </Typography>
        </Stack>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Scanning Progress
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{ height: 8, borderRadius: 4, mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {fullyScannedCount} of {totalItems} items fully scanned ({progressPercent.toFixed(1)}%)
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight="600" color="primary.main">
                      {totalScannedQty}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Scanned
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight="600" color="text.secondary">
                      {totalExpectedQty}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Expected
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                inputRef={scannerInputRef}
                fullWidth
                label="Scan Barcode"
                placeholder="Scan or enter barcode..."
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleScan(scanInput);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Scanner color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: scanInput && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setScanInput('')}>
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoFocus
              />
              <Button
                variant="contained"
                onClick={() => handleScan(scanInput)}
                disabled={!scanInput.trim()}
                sx={{ minWidth: 120 }}
              >
                Scan
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Items to Validate"
            subheader={`${filteredItems.length} items`}
            action={
              <TextField
                size="small"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            }
          />
          <CardContent>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="50px">Status</TableCell>
                    <TableCell>Item Details</TableCell>
                    <TableCell width="100px" align="center">Expected</TableCell>
                    <TableCell width="180px" align="center">Scanned</TableCell>
                    <TableCell width="150px">Box Reference</TableCell>
                    <TableCell width="50px"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.map((item) => {
                    const scanned = scannedItems.get(item.barcode);
                    const scannedQty = scanned?.quantity || 0;

                    const status = getScanStatus(scannedQty, item.expectedQuantity);
                    const isExpanded = expandedRows.has(item.barcode);
                    const atMax = scannedQty >= item.expectedQuantity;

                    return (
                      <React.Fragment key={item.barcode}>
                        <TableRow sx={{ bgcolor: rowBgFor(status) }}>
                          <TableCell>
                            {status === 'exact' ? (
                              <CheckCircle color="success" />
                            ) : status === 'partial' ? (
                              <Warning color="warning" />
                            ) : (
                              <RadioButtonUnchecked color="disabled" />
                            )}
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {item.itemName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {item.symbol}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {item.barcode}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              label={item.expectedQuantity}
                              size="small"
                              color="default"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                              <IconButton
                                size="small"
                                onClick={() => handleQuantityChange(item.barcode, scannedQty - 1)}
                                disabled={scannedQty === 0}
                                aria-label="decrease quantity"
                              >
                                -
                              </IconButton>

                              <Chip
                                label={scannedQty}
                                size="small"
                                color={chipColorFor(status)}
                                sx={{ fontWeight: 600, minWidth: '56px' }}
                              />

                              <Tooltip title={atMax ? `Max ${item.expectedQuantity}` : ''}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleQuantityChange(item.barcode, scannedQty + 1)}
                                    disabled={atMax}
                                    aria-label="increase quantity"
                                  >
                                    +
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">{item.boxRefNumber || '-'}</Typography>
                          </TableCell>

                          <TableCell>
                            {item.itemAdditionalProperties &&
                              Object.keys(item.itemAdditionalProperties).length > 0 && (
                                <IconButton size="small" onClick={() => toggleRowExpansion(item.barcode)}>
                                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              )}
                          </TableCell>
                        </TableRow>

                        {isExpanded && item.itemAdditionalProperties && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ py: 0 }}>
                              <Collapse in={isExpanded}>
                                <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Item Properties
                                  </Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                    {Object.entries(item.itemAdditionalProperties).map(([key, value]) => (
                                      <Chip
                                        key={key}
                                        label={`${key}: ${String(value)}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ mb: 0.5 }}
                                      />
                                    ))}
                                  </Stack>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredItems.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">No items found</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate(`/client/${clientId}/reception`)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Done />}
            onClick={() => setConfirmDialogOpen(true)}
            disabled={scannedItems.size === 0}
          >
            Validate Reception ({scannedItems.size} items)
          </Button>
        </Box>

        <Dialog open={confirmDialogOpen} onClose={() => !validating && setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Validation</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Alert severity="info">
                You are about to validate {scannedItems.size} items with a total quantity of {totalScannedQty}.
                This will move the scanned items to actual inventory.
              </Alert>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this reception..."
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Summary:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Total Items: {scannedItems.size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Total Quantity: {totalScannedQty}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Fully Scanned: {fullyScannedCount} / {totalItems}
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)} disabled={validating}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleValidate}
              disabled={validating}
              startIcon={validating ? <CircularProgress size={20} /> : <Done />}
            >
              {validating ? 'Validating...' : 'Confirm Validation'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ClientReceptionValidation;
